import { faHeadset } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { inboundRingsComputers } from "@phone/phoneSetupClient";
import {
  disableSoftphone,
  enableSoftphone,
  focusOwningTab,
  requestHeadsetTakeover,
  setMicrophone,
  setSpeaker,
  SPEAKER_SELECTION_SUPPORTED,
  useSoftphoneStore,
} from "@phone/softphone";
import { useOutboundLines } from "@phone/useOutboundLines";
import {
  Button,
  cn,
  Select,
  type SelectOption,
  StatusMark,
  Toggle,
} from "@scribenote/brian-design-system";

const STATUS_WORD = {
  off: "Off",
  connecting: "Connecting…",
  ready: "Ready",
  error: "Not connected",
} as const;

const DEFAULT_DEVICE = "__default__";

const sinceLabel = (ms: number): string => {
  const minutes = Math.max(0, Math.round((Date.now() - ms) / 60_000));
  if (minutes < 1) return "just now";
  if (minutes === 1) return "1 minute ago";
  if (minutes < 60) return `${minutes} minutes ago`;
  const hours = Math.round(minutes / 60);
  return hours === 1 ? "1 hour ago" : `${hours} hours ago`;
};

/** The "since when" note shows regardless: a browser may refuse to focus the owning tab. */
const NonOwnerNotice = () => {
  const ownerSince = useSoftphoneStore((s) => s.ownerSince);
  const takeoverRefused = useSoftphoneStore((s) => s.takeoverRefused);

  return (
    <div className="flex flex-col gap-2" data-testid="headset-non-owner">
      <p className="text-sm text-ink-body">
        Another tab owns this computer's headset
        {ownerSince ? `, took it over ${sinceLabel(ownerSince)}.` : "."}
      </p>
      <div className="flex flex-wrap gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={focusOwningTab}
          data-testid="headset-focus-owner"
        >
          Use that tab
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={requestHeadsetTakeover}
          data-testid="headset-take-over"
        >
          Use this tab instead
        </Button>
      </div>
      {takeoverRefused && (
        <p
          className="text-fine-print text-ink-stopped"
          data-testid="headset-takeover-refused"
        >
          {takeoverRefused}
        </p>
      )}
    </div>
  );
};

/**
 * The toggle reflects the registration, not the form. When the clinic rings
 * computers or calls from them by default there is no toggle: `SoftphoneBridge`
 * registers and retries on a backoff, and Try again skips that wait.
 */
export const HeadsetCard = ({ className }: { className?: string }) => {
  const offered = useOutboundLines();
  const inboundHere =
    offered.status === "ready" && inboundRingsComputers(offered.inboundRing);
  const automatic =
    inboundHere ||
    (offered.status === "ready" && offered.outboundComputerDefault);
  const status = useSoftphoneStore((s) => s.status);
  const error = useSoftphoneStore((s) => s.error);
  const ownership = useSoftphoneStore((s) => s.ownership);
  const microphones = useSoftphoneStore((s) => s.microphones);
  const speakers = useSoftphoneStore((s) => s.speakers);
  const microphoneId = useSoftphoneStore((s) => s.microphoneId);
  const speakerId = useSoftphoneStore((s) => s.speakerId);
  const microphoneError = useSoftphoneStore((s) => s.microphoneError);
  const speakerError = useSoftphoneStore((s) => s.speakerError);
  const on = status === "connecting" || status === "ready";
  const isOwner = ownership !== "non-owner";

  const options = (
    devices: { id: string; label: string }[],
  ): SelectOption[] => [
    { value: DEFAULT_DEVICE, label: "Browser default" },
    ...devices.map((device) => ({ value: device.id, label: device.label })),
  ];
  const chosen = (id: string | null) => id ?? DEFAULT_DEVICE;
  const pick = (value: string) => (value === DEFAULT_DEVICE ? null : value);

  if (!isOwner) {
    return (
      <section
        className={cn("flex flex-col gap-3", className)}
        data-testid="headset-card"
      >
        <div className="flex items-center justify-between gap-3">
          <h2 className="font-mono text-fine-print uppercase tracking-[0.14em] text-annotation">
            Computer headset
          </h2>
          <StatusMark
            tone="neutral"
            icon={<FontAwesomeIcon icon={faHeadset} aria-hidden="true" />}
            data-testid="headset-status"
          >
            Another tab
          </StatusMark>
        </div>
        <NonOwnerNotice />
      </section>
    );
  }

  return (
    <section
      className={cn("flex flex-col gap-3", className)}
      data-testid="headset-card"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="font-mono text-fine-print uppercase tracking-[0.14em] text-annotation">
          Computer headset
        </h2>
        <StatusMark
          tone={status === "ready" ? "accent" : "neutral"}
          icon={<FontAwesomeIcon icon={faHeadset} aria-hidden="true" />}
          data-testid="headset-status"
        >
          {STATUS_WORD[status]}
        </StatusMark>
      </div>
      {automatic ? (
        status === "error" && (
          <Button
            variant="outline"
            size="sm"
            className="self-start"
            onClick={() => void enableSoftphone({ persist: false })}
            data-testid="headset-retry"
          >
            Try again
          </Button>
        )
      ) : (
        <Toggle
          checked={on}
          onCheckedChange={(checked) => {
            if (checked) void enableSoftphone();
            else disableSoftphone();
          }}
          label="Use headset for calls"
          data-testid="headset-toggle"
        />
      )}
      {status === "ready" && (
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1">
            <Select
              label="Microphone"
              value={chosen(microphoneId)}
              onValueChange={(value) => setMicrophone(pick(value))}
              options={options(microphones)}
              data-testid="headset-microphone"
            />
            {microphoneError && (
              <p
                className="text-fine-print text-ink-stopped"
                data-testid="headset-microphone-error"
              >
                {microphoneError}
              </p>
            )}
          </div>
          <div className="flex flex-col gap-1">
            <Select
              label="Speaker"
              value={chosen(speakerId)}
              onValueChange={(value) => setSpeaker(pick(value))}
              options={options(speakers)}
              disabled={!SPEAKER_SELECTION_SUPPORTED}
              data-testid="headset-speaker"
            />
            {(speakerError || (!SPEAKER_SELECTION_SUPPORTED && speakerId)) && (
              <p
                className="text-fine-print text-ink-stopped"
                data-testid="headset-speaker-error"
              >
                {speakerError ??
                  "This browser can't switch speakers; using the default."}
              </p>
            )}
          </div>
        </div>
      )}
      <p
        className={cn(
          "text-fine-print",
          status === "error" ? "text-ink-stopped" : "text-annotation",
        )}
        data-testid="headset-note"
      >
        {status === "error" && error
          ? error
          : status === "ready"
            ? inboundHere
              ? "Connected for this clinic. Incoming calls ring here; calls placed here answer in the browser, no press 1."
              : automatic
                ? "Connected for this clinic. Calls placed here answer in the browser, no press 1."
                : "Brian can ring this computer. Calls placed here answer in the browser, no press 1."
            : automatic
              ? "Connecting for this clinic."
              : "Brian rings your desk phone until this is on."}
      </p>
      {ownership === "unavailable" && (
        <p
          className="text-fine-print text-annotation"
          data-testid="headset-coordination-unavailable"
        >
          This browser can't coordinate the headset across tabs. Keep it open in
          one tab at a time.
        </p>
      )}
    </section>
  );
};
